import React, { useEffect, useMemo, useState } from "react";
import styles from "../../Components/EditDiv/EditDiv.module.css";
import buttonStyles from "../../Components/YesNoButtonsCSS/YesNoButtons.module.css";
import { Grid } from "@mui/material";
import { useDispatch, useSelector } from "../../redux/store";
import { deleteEmailRoutingRule, updateEmailRouting } from "../../redux/slices/settings";
import DropDownV2 from "../../Components/DropDownBox/DropdownV2";

// A lightweight, flexible form to manage who receives which emails
// Looks and behaves like the Standards/Templates popup
const EmailRoutingForm = ({ initialRules = [], onSubmit, onClose }) => {
	const dispatch = useDispatch();
	const { configuration, emailRoutingRules, emailRoutingIndividuals, emailRoutingEvents, emailRoutingAliases } = useSelector((state) => state.settings);

	// Build selectable recipients list from individuals plus common roles
	const baseRecipientOptions = useMemo(() => {
		const aliasOptions = (emailRoutingAliases || []).map((a) => ({
			value: `preset:${a.name || a.display_name || a.id}`,
			label: `Alias: ${a.display_name || a.name || a.id}`,
			type: 'preset',
			recipients: Array.isArray(a.recipients) ? a.recipients.map((rec) => ({
				value: `${rec.recipient_type}:${rec.recipient_value}`,
				label: rec.recipient_value,
				type: rec.recipient_type,
			})) : [],
		}));
		const individualsFromConfig = (configuration?.individuals || []).map((p) => ({ value: `user:${p.id}`, label: p.name, type: 'user' }));
		const individualsFromEmailRouting = (emailRoutingIndividuals || []).map((p) => ({ value: `user:${p.id}`, label: p.name, type: 'user' }));
		const individuals = [...individualsFromConfig, ...individualsFromEmailRouting];
		const roles = [
			{ value: 'role:Admin', label: 'Admin', type: 'role' },
			{ value: 'role:Holiday Approver', label: 'Holiday Approver', type: 'role' },
			{ value: 'role:Finance', label: 'Finance', type: 'role' },
			// { value: 'role:Project Runner', label: 'Project Runner', type: 'role' },
			// { value: 'role:Line Manager', label: 'Line Manager', type: 'role' },
			{ value: 'role:All Team Leads', label: 'All Team Leads', type: 'role' },
		];

		// Special runtime recipients (visible, immutable)
		const special = [
			{ value: 'special:dynamic_user', label: 'Dynamic Specific User', type: 'special', immutable: true },
		];

		// Preset recipient groups from configuration
		const rawPresets = configuration?.presetRecipients || [];
		let presets = [];
		if (Array.isArray(rawPresets)) {
			// Expect objects like { name, recipients }
			presets = rawPresets
				.filter((g) => g && (g.name || g.label) && Array.isArray(g.recipients))
				.map((g) => ({
					value: `preset:${g.name || g.label}`,
					label: `Group: ${g.name || g.label}`,
					type: 'preset',
					recipients: g.recipients,
				}));
		} else if (rawPresets && typeof rawPresets === 'object') {
			// Object map: { groupName: recipients[] }
			presets = Object.keys(rawPresets).map((name) => ({
				value: `preset:${name}`,
				label: `Group: ${name}`,
				type: 'preset',
				recipients: rawPresets[name],
			}));
		}

		return [...aliasOptions, ...presets, ...roles, ...special, ...individuals];
	}, [configuration, emailRoutingIndividuals, emailRoutingAliases]);

	const [rules, setRules] = useState(() => {
		if (Array.isArray(initialRules) && initialRules.length > 0) return initialRules;
		return [];
	});

	// Build one rule per event; prefill from any existing rule lists
	useEffect(() => {
		const candidates = [];
		if (Array.isArray(initialRules)) candidates.push(...initialRules);
		if (Array.isArray(emailRoutingRules)) candidates.push(...emailRoutingRules);
		if (Array.isArray(configuration?.rules)) candidates.push(...configuration.rules);

		const normalizeRecipients = (rawRecipients) => {
			const list = Array.isArray(rawRecipients) ? rawRecipients : [];

			const matchUserById = (id) => {
				const byPrefixed = baseRecipientOptions.find((opt) => opt.value === `user:${id}`);
				if (byPrefixed) return byPrefixed;
				const byRaw = baseRecipientOptions.find((opt) => `${opt.value}` === `${id}` || `${opt.label}` === `${id}`);
				if (byRaw) return byRaw;
				return { value: `user:${id}`, label: `${id}`, type: 'user' };
			};

			const normalizeStringRecipient = (raw) => {
				if (!raw || typeof raw !== 'string') return [];

				const parts = raw.split(';').map((p) => p.trim()).filter(Boolean);
				const tokens = parts.length > 1 ? parts : [raw];

				return tokens.map((token) => {
					const directMatch = baseRecipientOptions.find((opt) => opt.value === token || opt.label === token);
					if (directMatch) return directMatch;

					if (token === 'dynamic_user' || token === 'special:dynamic_user') {
						const specialOpt = baseRecipientOptions.find((opt) => opt.value === 'special:dynamic_user');
						return specialOpt || { value: 'special:dynamic_user', label: 'Dynamic Specific User', type: 'special' };
					}

					if (token.startsWith('role:')) {
						const value = token.slice('role:'.length);
						const prefixed = `role:${value}`;
						const opt = baseRecipientOptions.find((o) => o.value === prefixed);
						return opt || { value: prefixed, label: value, type: 'role' };
					}

					if (token.startsWith('user:')) {
						const id = token.slice('user:'.length);
						return matchUserById(id);
					}

					if (token.startsWith('email:')) {
						const email = token.slice('email:'.length);
						return { value: `email:${email}`, label: email, type: 'email' };
					}

					if (/^\d+$/.test(token)) {
						return matchUserById(token);
					}

					if (token.includes('@')) {
						return { value: `email:${token}`, label: token, type: 'email' };
					}

					return { value: `role:${token}`, label: token, type: 'role' };
				});
			};

			return list.flatMap((r) => {
				if (r && typeof r === 'object' && 'value' in r && 'label' in r) return [r];
				if (r && typeof r === 'object' && (r.recipient_type || r.type)) {
					const type = r.recipient_type || r.type;
					const value = r.recipient_value || r.value || r.label;
					const prefixed = `${type}:${value}`;
					const match = baseRecipientOptions.find((opt) => opt.value === prefixed);
					return [match || { value: prefixed, label: value, type }];
				}
				if (typeof r === 'string') {
					const match = baseRecipientOptions.find((opt) => opt.value === r);
					if (match) return [match];
					return normalizeStringRecipient(r);
				}
				return [r];
			});
		};

		const byEventId = new Map(candidates.map((r) => [r.event_id ?? r.eventId ?? r.event?.id ?? r.event, r]));
		const out = (emailRoutingEvents || []).map((ev, idx) => {
			const existing = byEventId.get(ev.id) || {};
			return {
				id: existing.id ?? (idx + 1),
				event_id: ev.id,
				recipients: normalizeRecipients(existing.recipients),
			};
		});
		setRules(out);
	}, [initialRules, emailRoutingRules, configuration, baseRecipientOptions, emailRoutingEvents]);

	const [emailInputs, setEmailInputs] = useState({});

	const handleAddRule = () => {
		const nextId = (rules[rules.length - 1]?.id || 0) + 1;
		setRules([...rules, { id: nextId, event_id: '', recipients: [] }]);
	};

	const handleRemoveRule = (id) => async () => {
		setRules((prev) => prev.map((r) => (r.id === id ? { ...r, recipients: [] } : r)));
		if (id != null) {
			try {
				await dispatch(deleteEmailRoutingRule(id));
			} catch (e) {}
		}
	};

	const handleEventChange = (id) => (opt) => {
		const eventId = opt?.value ?? '';
		setRules(rules.map((r) => (r.id === id ? { ...r, event_id: eventId } : r)));
	};

	// Expand preset groups to concrete recipients (roles/users/emails)
	const expandPresetRecipients = (items) => {
		const optionsIndex = new Map(baseRecipientOptions.map((o) => [o.value, o]));
		const out = [];
		for (const it of items) {
			if (it && (it.type === 'preset' || (typeof it === 'string' && it.startsWith('preset:')))) {
				const presetValue = typeof it === 'string' ? it : it.value;
				const presetOpt = baseRecipientOptions.find((o) => o.value === presetValue);
				const recipients = presetOpt?.recipients || it?.recipients || [];
				for (const rec of recipients) {
					if (typeof rec === 'string') {
						if (rec.startsWith('user:')) {
							const userOpt = optionsIndex.get(rec);
							if (userOpt) out.push(userOpt);
						} else if (rec.startsWith('role:')) {
							const roleOpt = optionsIndex.get(rec);
							if (roleOpt) out.push(roleOpt);
						} else if (rec.startsWith('email:')) {
							const email = rec.slice('email:'.length);
							out.push({ value: `email:${email}`, label: email, type: 'email' });
						} else if (rec.includes('@')) {
							out.push({ value: `email:${rec}`, label: rec, type: 'email' });
						}
					} else if (rec && typeof rec === 'object') {
						const key = rec.value || rec.id || rec.label;
						const opt = optionsIndex.get(key);
						out.push(opt || rec);
					}
				}
			} else {
				out.push(it);
			}
		}
		// Deduplicate by normalized key
		const seen = new Set();
		return out.filter((x) => {
			const key = x?.value || x?.id || x?.label;
			if (!key) return false;
			if (seen.has(key)) return false;
			seen.add(key);
			return true;
		});
	};

	const handleRecipientsChange = (id) => (newSelected) => {
		const expanded = expandPresetRecipients(newSelected || []);
		setRules(rules.map((r) => (r.id === id ? { ...r, recipients: expanded } : r)));
	};

	const handleEmailInputChange = (id) => (e) => {
		setEmailInputs({ ...emailInputs, [id]: e.target.value });
	};

	const handleAddEmailRecipient = (id) => () => {
		const email = (emailInputs[id] || '').trim();
		if (!email) return;
		const emailOption = { value: `email:${email}`, label: email, type: 'email' };
		setRules(rules.map((r) => (r.id === id ? { ...r, recipients: [...r.recipients, emailOption] } : r)));
		setEmailInputs({ ...emailInputs, [id]: '' });
	};

	const handleSubmit = async () => {
		// Normalize payload and persist
		if (onSubmit) {
			onSubmit(rules);
		} else {
			await dispatch(updateEmailRouting(rules));
		}
		onClose?.();
	};

	const stopPropagation = (e) => e.stopPropagation();

	return (
		<div className={styles.backGround} onClick={onClose}>
			<div
				className={styles.form}
				style={{ position: 'relative', top: 'auto', width: '1100px', maxWidth: '95vw', height: '85vh', overflow: 'hidden', alignItems: 'stretch', paddingBottom: '12px', display: 'flex', flexDirection: 'column' }}
				onClick={stopPropagation}
			>
				<div className={styles.momentTitle}>MOMENT</div>
				<div className={styles.sectionTitle}>{`Email Notifications`}</div>
				<p style={{ marginTop: '-6px', marginBottom: '6px', color: '#666', fontFamily: 'PT Sans' }}>
					Define rules mapping an event to one or more recipients. You can select users, roles, or add external emails.
				</p>

				<div style={{ flex: 1, overflowY: 'auto', paddingRight: '6px' }}>
					{rules.map((rule) => (
						<div key={rule.id} style={{ border: '2px solid #ECEEF7', background: '#fff', borderRadius: '8px', padding: '8px', marginTop: '8px' }}>
							<Grid container spacing={1} sx={{ width: '100%', alignItems: 'center' }}>
								<Grid item xs={12} md={5}>
									<p className={styles.dataTitleV2} style={{ marginBottom: '2px' }}>Event</p>
									{(() => {
										const ev = (emailRoutingEvents || []).find((e) => `${e.id}` === `${rule.event_id}`);
										const eventName = ev?.display_name || ev?.event_code || '';
										return (
											<div style={{ padding: '6px 10px', border: '1px solid #cbd5e1', borderRadius: 4, minHeight: 30, display: 'flex', alignItems: 'center' }}>
											<span>{eventName}</span>
										</div>
									);
									})()}
								</Grid>
								<Grid item xs={12} md={7}>
									<p className={styles.dataTitleV2} style={{ marginBottom: '2px' }}>Recipients</p>
									<DropDownV2
										options={baseRecipientOptions}
										initialSelectedValue={Array.isArray(rule.recipients) ? rule.recipients.map(r => {
											if (r && typeof r === 'object' && 'value' in r && 'label' in r) return r;
											if (r && typeof r === 'object' && (r.recipient_type || r.type)) {
												const type = r.recipient_type || r.type;
												const value = r.recipient_value || r.value || r.label;
												return { value: `${type}:${value}`, label: value, type };
											}
											if (typeof r === 'string') return { value: r, label: r.includes(':') ? r.split(':')[1] : r };
											return r;
										}) : []}
										onValueChange={handleRecipientsChange(rule.id)}
										mode={'multi'}
										expandSelected={true}
										placeholder={'Select users, roles...'}
									/>
									<div style={{ display: 'flex', gap: '8px', marginTop: '6px', alignItems: 'center' }}>
										<input
											type="email"
											placeholder="Add external email (optional)"
											value={emailInputs[rule.id] || ''}
											onChange={handleEmailInputChange(rule.id)}
											className={styles.InputBox}
											style={{ flex: 1, height: '30px' }}
										/>
										<div className={buttonStyles.YesButtonComponentStyling} onClick={handleAddEmailRecipient(rule.id)} style={{ width: '80px', textAlign: 'center' }}>ADD</div>
										<div className={buttonStyles.NoButtonComponentStyling} onClick={handleRemoveRule(rule.id)} style={{ width: '90px', textAlign: 'center' }}>REMOVE</div>
									</div>
								</Grid>
							</Grid>
						</div>
					))}
				</div>

				{/* No Add Rule button; every event is listed automatically */}

				<div className={styles.updateAndCancel} style={{ justifyContent: 'flex-end' }}>
					<div className={styles.updateButton} onClick={handleSubmit}>
						<div className={buttonStyles.YesButtonComponentStyling}>SUBMIT</div>
					</div>
					<div className={styles.cancelButton} onClick={onClose}>
						<div className={buttonStyles.NoButtonComponentStyling}>CANCEL</div>
					</div>
				</div>
			</div>
		</div>
	);
};

export default EmailRoutingForm;


